import { useEffect, useState } from 'react';
import { User } from 'lucide-react';
import { Assignee } from '../../types/mold';
import { userService } from '../../services/api/users';
import { getAvatarBlob } from '../../services/avatarCache';

// interface Assignee {
//   img?: string;
//   name?: string;
// }

interface UserProfileImageProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  currentAssignee: Assignee | null;
}

const UserProfileImage: React.FC<UserProfileImageProps> = ({
  size = 'md',
  currentAssignee,
}) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(currentAssignee?.img ?? null);
  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'xs':
        return 'h-5 w-5';
      case 'sm':
        return 'h-8 w-8';
      case 'lg':
        return 'h-16 w-16';
      default:
        return 'h-10 w-10';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const sizeClasses = getSizeClasses(size);

  useEffect(() => {
    let objectUrl: string | null = null;
    let active = true;
    let requestId = 0;

    const loadAvatar = async () => {
      const thisRequest = ++requestId;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      if (!currentAssignee?.id || currentAssignee.img) {
        setAvatarUrl(currentAssignee?.img ?? null);
        return;
      }
      try {
        const blob = await getAvatarBlob(
          currentAssignee.id,
          // The unique version only applies to a cache miss; every simultaneous
          // component then shares this same protected request.
          () => userService.getAvatar(currentAssignee.id, Date.now()).then((response) => response.data),
        );
        if (!blob || !active || thisRequest !== requestId) return;
        objectUrl = URL.createObjectURL(blob);
        setAvatarUrl(objectUrl);
      } catch {
        if (active && thisRequest === requestId) setAvatarUrl(null);
      }
    };

    loadAvatar();
    const refresh = (event: Event) => {
      const updatedUserId = (event as CustomEvent<{ userId?: number }>).detail?.userId;
      if (updatedUserId === currentAssignee?.id) loadAvatar();
    };
    window.addEventListener('avatar-updated', refresh);
    return () => {
      active = false;
      requestId += 1;
      window.removeEventListener('avatar-updated', refresh);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [currentAssignee?.id, currentAssignee?.img]);

  if (!avatarUrl) {
    if (currentAssignee?.name) {
      // Show initials if name exists
      return (
        <div
          className={`${sizeClasses} inline-flex items-center justify-center rounded-full bg-gray-100 ring-2 ring-white`}
        >
          <span className="text-sm font-medium text-gray-600">
            {getInitials(currentAssignee.name)}
          </span>
        </div>
      );
    } else {
      // Show default user icon if no name and no image
      return (
        <div
          className={`${sizeClasses} inline-flex items-center justify-center rounded-full bg-gray-100 ring-2 ring-white`}
        >
          <User className="h-1/2 w-1/2 text-gray-500" />
        </div>
      );
    }
  }

  // Show actual image if it exists
  return (
    <img
      className={`inline-block ${sizeClasses} rounded-full ring-2 ring-white object-cover`}
      src={avatarUrl}
      alt={currentAssignee?.name || 'User avatar'}
    />
  );
};

export default UserProfileImage;
